import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import icLogo from "../images/ic.png";

export default function PinVerification() {
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  useEffect(() => {
    // Get pending auth data
    const pendingAuth = sessionStorage.getItem("pendingAuth");
    if (!pendingAuth) {
      navigate("/login");
      return;
    }
    const user = JSON.parse(pendingAuth);
    setUserName(user.name);
    
    // Focus first input
    inputRefs.current[0]?.focus();
  }, [navigate]);

  const handlePinChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (index === 5 && value) {
      const fullPin = newPin.join("");
      if (fullPin.length === 6) {
        setTimeout(() => handlePinSubmit(fullPin), 300);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newPin = [...pin];
    pastedData.split("").forEach((digit, i) => {
      if (i < 6) newPin[i] = digit;
    });
    setPin(newPin);

    // Focus last filled input or last input
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if 6 digits pasted
    if (pastedData.length === 6) {
      setTimeout(() => handlePinSubmit(pastedData), 300);
    }
  };

  const handlePinSubmit = (pinValue = null) => {
    const fullPin = pinValue || pin.join("");
    
    if (fullPin.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setIsLoading(true);
    setError("");

    setTimeout(() => {
      const pendingAuth = sessionStorage.getItem("pendingAuth");
      if (!pendingAuth) {
        navigate("/login");
        return;
      }

      const user = JSON.parse(pendingAuth);

      if (fullPin === user.pin) {
        const authData = {
          email: user.email,
          name: user.name,
          loginTime: Date.now(),
          expiresAt: Date.now() + (24 * 60 * 60 * 1000)
        };

        localStorage.setItem("authSession", JSON.stringify(authData));
        sessionStorage.removeItem("pendingAuth");
        
        navigate("/");
      } else {
        setError("Invalid PIN. Please try again.");
        setPin(["", "", "", "", "", ""]);
        setIsLoading(false);
        inputRefs.current[0]?.focus();
      }
    }, 800);
  };

  const handleBack = () => {
    sessionStorage.removeItem("pendingAuth");
    navigate("/login");
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Animated background shapes */}
      <div style={{
        position: "absolute",
        width: "400px",
        height: "400px",
        background: "rgba(255, 255, 255, 0.1)",
        borderRadius: "50%",
        top: "-200px",
        right: "-200px",
        animation: "float 6s ease-in-out infinite"
      }}></div>
      <div style={{
        position: "absolute",
        width: "300px",
        height: "300px",
        background: "rgba(255, 255, 255, 0.08)",
        borderRadius: "50%",
        bottom: "-150px",
        left: "-150px",
        animation: "float 8s ease-in-out infinite reverse"
      }}></div>

      <div style={{ width: "100%", maxWidth: "460px", margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{
          background: "rgba(255, 255, 255, 0.98)",
          padding: "clamp(26px, 4vw, 44px)",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.3)"
        }}>
          {/* Lock Icon */}
          <div style={{
            textAlign: "center",
            marginBottom: "20px"
          }}>
            <div style={{
            width: "150px",
              height: "100px",
              margin: "0 auto 20px",
               background: "transparent",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            boxShadow: "none",
              fontSize: "26px",
              fontWeight: "700",
              color: "#fff"
            }}>
               <img
                             src={icLogo}
                             alt="Infinnity Capital"
                             style={{
                               width: "200px",
                               height: "206px",
                               objectFit: "contain"
                             }}
                           />
            </div>
          </div>

          <h2 style={{
            textAlign: "center",
            marginBottom: "28px",
            background: "linear-gradient(135deg, #FFD700, #FFA500)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
            fontSize: "clamp(22px, 4vw, 30px)",
            lineHeight: "1.25"
          }}>
            Mint Platform
          </h2>
          <p style={{ 
            textAlign: "center", 
            marginBottom: "8px", 
            color: "#333",
            fontSize: "17px",
            fontWeight: "600"
          }}>
            PIN Verification
          </p>
          <p style={{ 
            textAlign: "center", 
            marginBottom: "30px", 
            color: "#666",
            fontSize: "14px"
          }}>
            Welcome back, {userName}. Enter your 6-digit PIN to continue.
          </p>
          
          {/* PIN Input Boxes */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            flexWrap: "nowrap",
            marginBottom: "30px"
          }}>
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                style={{
                  width: "clamp(40px, 11vw, 56px)",
                  height: "clamp(50px, 13vw, 64px)",
                  fontSize: "clamp(22px, 5vw, 30px)",
                  fontWeight: "bold",
                  textAlign: "center",
                  border: error 
                    ? "3px solid #d32f2f" 
                    : digit 
                      ? "3px solid #667eea" 
                      : "3px solid #e0e0e0",
                  borderRadius: "12px",
                  background: digit ? "#f0f4ff" : "#f8f9fa",
                  transition: "all 0.3s ease",
                  outline: "none",
                  color: "#333",
                  cursor: isLoading ? "not-allowed" : "text"
                }}
                onFocus={(e) => {
                  if (!error) e.target.style.border = "3px solid #667eea";
                  e.target.style.transform = "scale(1.05)";
                }}
                onBlur={(e) => {
                  if (!error && !digit) e.target.style.border = "3px solid #e0e0e0";
                  e.target.style.transform = "scale(1)";
                }}
              />
            ))}
          </div>

          {error && (
            <div style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              textAlign: "center",
              fontSize: "14px",
              fontWeight: "500",
              border: "1px solid #ffcdd2",
              animation: "shake 0.5s"
            }}>
              {error}
            </div>
          )}

          {isLoading && (
            <div style={{
              textAlign: "center",
              marginBottom: "20px",
              color: "#667eea",
              fontSize: "16px",
              fontWeight: "500"
            }}>
              <span style={{ 
                display: "inline-block",
                width: "20px",
                height: "20px",
                border: "3px solid #667eea",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                marginRight: "10px",
                verticalAlign: "middle"
              }}></span>
              Verifying PIN...
            </div>
          )}

          <Button
            variant="outline-secondary"
            onClick={handleBack}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "14px",
              fontSize: "16px",
              borderRadius: "12px",
              border: "2px solid #e0e0e0",
              color: "#666",
              fontWeight: "600",
              transition: "all 0.3s ease",
              cursor: isLoading ? "not-allowed" : "pointer"
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.border = "2px solid #667eea";
                e.target.style.color = "#667eea";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.border = "2px solid #e0e0e0";
                e.target.style.color = "#666";
              }
            }}
          >
            ← Back to Login
          </Button>

          <p style={{
            textAlign: "center",
            marginTop: "30px",
            color: "#999",
            fontSize: "13px"
          }}>
            Session valid for 24 hours
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
      `}</style>
    </div>
  );
}
